import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getBlog, fetchBlogs } from "../../../api/FetchBlogs";
import { parseDescription } from "../../shared/Utils/ParseDescription";
import Slider from "react-slick";
import { ArrowUpRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import BlogCard from "../BlogSearch/BlogCard/BlogCard";

function BlogPage() {
  const { title } = useParams();
  const [blog, setBlog] = useState({});
  const [videoUrls, setVideoUrls] = useState([]);
  const [otherBlogs, setOtherBlogs] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef(null);

  let validatedUrls = [];

  useEffect(() => {
    const fetchInfo = async () => {
      getBlog(title).then((response) => {
        setBlog(response);
      });
    };
    fetchInfo();
  }, [title]);

  useEffect(() => {
    const fetchOtherBlogs = async () => {
      try {
        const allBlogs = await fetchBlogs();
        // Filter out the current blog and get up to 6 other blogs
        const filtered = allBlogs
          .filter((b) => b.title !== blog.title)
          .slice(0, 6);
        setOtherBlogs(filtered);
      } catch (error) {
        console.error("Error fetching other blogs:", error);
      }
    };

    if (blog.title) {
      fetchOtherBlogs();
    }
  }, [blog.title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Checking the URL against regex
  const regex = new RegExp(/^(https?:\/\/)/);

  // Tests against valid URL and changes URL if
  const validateUrls = (urls) => {
    if (urls) {
      validatedUrls = urls.split(",");
      for (let i = 0; i < validatedUrls.length; i++) {
        validatedUrls[i] = validatedUrls[i].trim();
        if (regex.test(validatedUrls[i])) {
          if (
            validatedUrls[i].includes("youtube") &&
            validatedUrls[i].indexOf("watch?v=") !== -1
          ) {
            validatedUrls[i] = validatedUrls[i].replace("watch?v=", "embed/");
            validatedUrls[i] = validatedUrls[i].slice(
              0,
              validatedUrls[i].indexOf("&")
            );
          }
        } else {
          validatedUrls[i] = "URL not valid";
        }
      }
    }
    setVideoUrls(validatedUrls);
  };

  useEffect(() => {
    if (blog.video_urls) {
      validateUrls(blog.video_urls);
    }
  }, [blog]);

  let slideshowSettings = {
    dots: true,
    infinite: blog.video_urls && videoUrls ? videoUrls.length > 1 : false,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: blog.video_urls && videoUrls ? videoUrls.length > 1 : false,
    autoplay: false,
    speed: 500,
  };

  const calculateReadingTime = (text) => {
    const wordsPerMinute = 225;
    const allText = [
      blog.paragraph_1,
      blog.paragraph_2,
      blog.paragraph_3,
      blog.paragraph_4,
      blog.paragraph_5,
      blog.paragraph_6,
      blog.paragraph_7,
      blog.paragraph_8,
      blog.paragraph_9,
      blog.paragraph_10,
    ]
      .filter(Boolean)
      .join(" ");

    const words = allText.replace(/<[^>]+>/g, "").split(/\s+/).length;
    const minutes = words / wordsPerMinute;
    return Math.ceil(minutes);
  };

  const totalReadingTime = calculateReadingTime();

  // Extract explicit heading text only (h1-h6) for table of contents
  const extractFirstSentence = (htmlText) => {
    if (!htmlText) return "";

    // Only treat h1-h6 tags as headings; ignore <strong> and other tags
    const headingMatch = htmlText.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
    if (headingMatch && headingMatch[1].trim()) {
      // For TOC, strip nested tags (e.g., spans/styles) to show plain text
      const plain = headingMatch[1].replace(/<[^>]+>/g, "").trim();
      return plain;
    }

    // No heading present
    return "";
  };

  // Generate table of contents from paragraphs
  const generateTableOfContents = () => {
    const toc = [];

    if (blog.paragraph_1) {
      const title = extractFirstSentence(blog.paragraph_1);
      if (title) {
        toc.push({
          id: "introduction",
          title: title,
          displayTitle:
            title.length > 40 ? title.substring(0, 40) + "..." : title,
        });
      }
    }

    for (let i = 2; i <= 10; i++) {
      const paragraph = blog[`paragraph_${i}`];
      if (paragraph) {
        const title = extractFirstSentence(paragraph);
        if (title) {
          toc.push({
            id: `section-${i}`,
            title: title,
            displayTitle:
              title.length > 40 ? title.substring(0, 40) + "..." : title,
          });
        }
      }
    }

    return toc;
  };

  const tableOfContents = generateTableOfContents();

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Get the element's position relative to the document
      const elementTop = element.offsetTop;
      // Add some offset to account for any fixed headers or padding
      const offset = 100; // Adjust this value as needed

      // Scroll to the element with smooth behavior
      window.scrollTo({
        top: elementTop - offset,
        behavior: "smooth",
      });
    } else {
      console.warn(`Element with ID '${sectionId}' not found`);
    }
  };

  // Extract and separate heading (h1-h6) from paragraph content; do not promote bold text
  const separateFirstLineFromContent = (htmlText) => {
    if (!htmlText) return { firstLine: "", remainingContent: "" };

    // Only consider h1-h6 tags as headings
    const headingMatch = htmlText.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
    if (headingMatch && headingMatch[1].trim()) {
      const firstLine = headingMatch[1].trim();
      const remainingContent = htmlText
        .replace(/<h[1-6][^>]*>.*?<\/h[1-6]>/i, "")
        .trim();
      return { firstLine, remainingContent };
    }

    // No explicit heading; keep all content as paragraph content
    return { firstLine: "", remainingContent: htmlText };
  };

  // Shared className variables for blog content - made responsive
  const blogSectionClasses =
    "blog-paragraph break-words overflow-wrap-break-word hyphens-auto mb-6 md:mb-8 lg:mb-10";
  const blogHeadingClasses =
    "font-poppins text-2xl sm:text-3xl lg:text-3xl font-semibold leading-normal mb-2 md:mb-6 text-black text-left";
  const blogContentClasses =
    "font-montserrat text-sm sm:text-base font-medium leading-relaxed text-black text-left";

  // Custom navigation functions for group-based scrolling
  const goToPreviousGroup = () => {
    if (sliderRef.current) {
      const currentWidth = window.innerWidth;
      const groupSize = currentWidth >= 1200 ? 3 : currentWidth >= 900 ? 2 : 1;
      const targetSlide = Math.max(0, currentSlide - groupSize);
      sliderRef.current.slickGoTo(targetSlide);
    }
  };

  const goToNextGroup = () => {
    if (sliderRef.current) {
      const currentWidth = window.innerWidth;
      const groupSize = currentWidth >= 1200 ? 3 : currentWidth >= 900 ? 2 : 1;
      const targetSlide = Math.min(
        otherBlogs.length - 1,
        currentSlide + groupSize
      );
      sliderRef.current.slickGoTo(targetSlide);
    }
  };

  // Custom arrow components
  const CustomPrevArrow = ({ className, style }) => (
    <button
      className={`${className} !flex !items-center !justify-center !z-10`}
      style={{
        position: "absolute",
        left: "8px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
      }}
      onClick={goToPreviousGroup}
    >
      <div
        className="w-8 h-8 min-w-[32px] min-h-[32px] border-2 border-blue-900 hover:bg-blue-900 rounded-full flex items-center justify-center transition-all duration-200 group"
        style={{ aspectRatio: "1/1" }}
      >
        <CaretLeft
          size={16}
          className="text-blue-900 group-hover:text-white transition-colors duration-200"
          weight="bold"
        />
      </div>
    </button>
  );

  const CustomNextArrow = ({ className, style }) => (
    <button
      className={`${className} !flex !items-center !justify-center !z-10`}
      style={{
        position: "absolute",
        right: "8px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
      }}
      onClick={goToNextGroup}
    >
      <div
        className="w-8 h-8 min-w-[32px] min-h-[32px] border-2 border-blue-900 hover:bg-blue-900 rounded-full flex items-center justify-center transition-all duration-200 group"
        style={{ aspectRatio: "1/1" }}
      >
        <CaretRight
          size={16}
          className="text-blue-900 group-hover:text-white transition-colors duration-200"
          weight="bold"
        />
      </div>
    </button>
  );

  // Calculate number of groups based on current viewport
  const getGroupSize = () => {
    const currentWidth = window.innerWidth;
    return currentWidth >= 1200 ? 3 : currentWidth >= 900 ? 2 : 1;
  };

  const getTotalGroups = () => {
    const groupSize = getGroupSize();
    return Math.ceil(otherBlogs.length / groupSize);
  };

  // Carousel settings for other blogs
  const blogCarouselSettings = {
    dots: false, // We'll create custom dots
    infinite: false,
    speed: 500,
    slidesToShow: Math.min(3, otherBlogs.length), // Show fewer if we have fewer blogs
    slidesToScroll: 1,
    arrows: otherBlogs.length > 3, // Only show arrows when more blogs than can be displayed
    autoplay: false,
    centerMode: otherBlogs.length < 3, // Center when fewer than 3 blogs
    centerPadding: "0px",
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(2, otherBlogs.length), // Show fewer if we have fewer blogs
          slidesToScroll: 1,
          infinite: false,
          arrows: otherBlogs.length > 2, // Only show arrows when more blogs than can be displayed
          centerMode: otherBlogs.length < 2, // Center when fewer than 2 blogs
          centerPadding: "0px",
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: false,
          arrows: otherBlogs.length > 1, // Only show arrows when more blogs than can be displayed
          centerMode: true, // Always center on mobile
          centerPadding: "0px",
        },
      },
    ],
  };

  // Custom dots for groups
  const renderGroupDots = () => {
    const groupSize = getGroupSize();
    const totalGroups = getTotalGroups();
    const currentGroup = Math.floor(currentSlide / groupSize);

    // Only show dots if there are multiple groups
    if (totalGroups <= 1) return null;

    return (
      <div className="flex justify-center items-center gap-2 mt-8">
        {Array.from({ length: totalGroups }, (_, i) => (
          <button
            key={i}
            className={`h-3 transition-all duration-200 border-0 p-0 ${
              i === currentGroup
                ? "w-8 bg-gray-800 rounded-full" // Active dot is longer (pill shape)
                : "w-3 bg-gray-300 hover:bg-gray-800 rounded-full" // Inactive dots are circles
            }`}
            onClick={() => sliderRef.current?.slickGoTo(i * groupSize)}
          >
            <span className="sr-only">Go to group {i + 1}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="main-div blog-page overflow-x-hidden break-words">
      {/* Hero Section */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-20 xl:px-32 py-4 sm:py-6 lg:py-12 xl:py-20">
        <div className="max-w-[1360px] w-full mx-auto">
          {/* Title */}
          <h1 className="text-orange text-left mb-6 sm:mb-8 md:mb-9 !font-poppins !text-3xl sm:!text-4xl md:!text-5xl !font-medium !leading-normal">
            {blog.title}
          </h1>

          {/* Subtitle/Description */}
          {blog.subtitle && (
            <p
              className="text-black mb-12 sm:mb-16 md:mb-20 !font-poppins !text-lg sm:!text-xl font-normal leading-relaxed text-left"
              dangerouslySetInnerHTML={{
                __html: blog.subtitle,
              }}
            />
          )}

          {/* Featured Image */}
          <div className="w-full mb-4">
            {blog.image_1 && (
              <img
                src={blog.image_1}
                alt={blog.title}
                className="w-full h-auto object-cover rounded-lg shadow-lg"
              />
            )}
          </div>

          {/* Date and Reading Time */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 font-poppins text-base sm:text-lg lg:text-xl font-normal leading-normal">
            <p>
              {new Date(blog.created_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <p>
              {totalReadingTime} {totalReadingTime === 1 ? "Minute" : "Minutes"}{" "}
              Read
            </p>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-20 xl:px-32 overflow-x-hidden">
        <div className="max-w-[1360px] w-full mx-auto overflow-x-hidden">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 xl:gap-20 overflow-x-hidden">
            {/* Table of Contents Sidebar */}
            <div className="hidden lg:block flex-shrink-0 w-full lg:w-80 xl:w-96">
              <div className="sticky top-8">
                <nav>
                  {tableOfContents.map((item, index) => (
                    <div
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`flex items-center justify-between py-3 lg:py-4 hover:opacity-70 cursor-pointer transition-all duration-200 group border-b border-gray-200 ${
                        index === 0 ? "border-t border-gray-200" : ""
                      }`}
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <h4 className="!font-montserrat !text-base lg:!text-lg !font-semibold !leading-normal text-left break-words overflow-wrap-break-word">
                          {item.title}
                        </h4>
                      </div>
                      <div className="transition-colors flex-shrink-0">
                        <ArrowUpRight size={18} weight="regular" />
                      </div>
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 max-w-none overflow-x-hidden break-words text-left">
              <div className="success-video-carousel mb-6 md:mb-8">
                {blog.video_urls && videoUrls ? (
                  <Slider {...slideshowSettings}>
                    {videoUrls.map((videoUrl, index) => (
                      <div key={index} className="carousel-slide">
                        <div
                          className="relative w-full"
                          style={{ paddingBottom: "56.25%" }}
                        >
                          <iframe
                            src={videoUrl}
                            title={`Success Video ${index + 1}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                ) : null}
              </div>

              {/* Main content starts from paragraph_1 (full content) */}
              {blog.paragraph_1 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_1);
                  return (
                    <div id="introduction" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_2 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_2}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_2 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_2);
                  return (
                    <div id="section-2" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_3 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_3}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_3 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_3);
                  return (
                    <div id="section-3" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_4 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_4}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_4 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_4);
                  return (
                    <div id="section-4" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_5 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_5}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_5 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_5);
                  return (
                    <div id="section-5" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_6 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_6}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_6 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_6);
                  return (
                    <div id="section-6" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_7 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_7}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_7 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_7);
                  return (
                    <div id="section-7" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_8 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_8}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_8 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_8);
                  return (
                    <div id="section-8" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_9 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_9}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_9 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_9);
                  return (
                    <div id="section-9" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
              {blog.image_10 && (
                <div className="blog-image mb-6 md:mb-8">
                  <img
                    src={blog.image_10}
                    alt=""
                    className="w-full h-auto object-cover rounded-lg shadow-md"
                  />
                </div>
              )}
              {blog.paragraph_10 &&
                (() => {
                  const { firstLine, remainingContent } =
                    separateFirstLineFromContent(blog.paragraph_10);
                  return (
                    <div id="section-10" className={blogSectionClasses}>
                      {firstLine && (
                        <h2 className={blogHeadingClasses}>{firstLine}</h2>
                      )}
                      {remainingContent && (
                        <div
                          className={blogContentClasses}
                          dangerouslySetInnerHTML={{
                            __html: parseDescription(remainingContent),
                          }}
                        />
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      </div>

      {/* Discover More Blogs Section */}
      {otherBlogs.length > 0 && (
        <div className="w-full py-12 md:py-16 lg:py-20 bg-gray-50">
          <div className="w-full px-4 sm:px-6 md:px-8 lg:px-20 xl:px-32">
            <div className="max-w-[1360px] w-full mx-auto">
              <h2 className="text-orange  mb-8 md:mb-12 !font-poppins !text-3xl text-left !font-medium !leading-normal">
                Discover More Blogs
              </h2>
            </div>
          </div>

          <div className="blog-carousel-container relative w-full">
            <style jsx>{`
              .slick-arrow:before {
                display: none !important;
              }
              .slick-track {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              .slick-slide {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
              }
              .slick-list {
                width: 100% !important;
              }
              .slick-slider {
                width: 100% !important;
              }
            `}</style>
            <div className="w-full px-4 sm:px-6 md:px-8 lg:px-20 xl:px-32">
              <Slider ref={sliderRef} {...blogCarouselSettings}>
                {otherBlogs.map((blogItem, index) => (
                  <div key={index} className="px-2">
                    <BlogCard
                      title={blogItem.title}
                      subtitle={blogItem.subtitle}
                      created_at={blogItem.created_at}
                      paragraph_1={blogItem.paragraph_1 || ""}
                      paragraph_2={blogItem.paragraph_2 || ""}
                      paragraph_3={blogItem.paragraph_3 || ""}
                      paragraph_4={blogItem.paragraph_4 || ""}
                      paragraph_5={blogItem.paragraph_5 || ""}
                      paragraph_6={blogItem.paragraph_6 || ""}
                      paragraph_7={blogItem.paragraph_7 || ""}
                      paragraph_8={blogItem.paragraph_8 || ""}
                      paragraph_9={blogItem.paragraph_9 || ""}
                      paragraph_10={blogItem.paragraph_10 || ""}
                      image_1={blogItem.image_1}
                    />
                  </div>
                ))}
              </Slider>
            </div>
            {renderGroupDots()}
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogPage;
